// Tabla de reembolsos de enganche de la tienda: cancelación, folio, vendedor, monto, cobro y estatus.
import { RefundStatusEnum } from '@fiado/type-kit/bin/retailWizard/enums/RefundStatusEnum';
import { Button } from '@/kit';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { StatusPill } from '@/shared/patterns/Pills';
import type { SellerPendingRefundItem } from '@fiado/type-kit/bin/retailWizard/dtos/SellerPendingRefundItem';
import {
    deliveredLabel,
    refundPaymentsLabel,
    refundReference,
    sortRefunds,
} from '../lib/refundsFormat';

export interface RefundsTableProps {
    refunds: readonly SellerPendingRefundItem[];
    canDeliver: boolean;
    /** Filas cuyo botón se ocultó tras un rechazo del silo, con la leyenda que lo sustituye. */
    blockedNotes: ReadonlyMap<string, string>;
    onDeliver: (refund: SellerPendingRefundItem) => void;
}

const TH = 'px-2 py-1.5 font-semibold';
const TD = 'whitespace-nowrap px-2 py-1.5 align-middle';

export function RefundsTable({ refunds, canDeliver, blockedNotes, onDeliver }: RefundsTableProps) {
    return (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full min-w-[760px] text-[12px]">
                <thead>
                    <tr className="border-b border-border text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                        <th scope="col" className={TH}>
                            Cancelada
                        </th>
                        <th scope="col" className={TH}>
                            Folio
                        </th>
                        <th scope="col" className={TH}>
                            Vendedor
                        </th>
                        <th scope="col" className={`${TH} text-right`}>
                            Monto
                        </th>
                        <th scope="col" className={TH}>
                            Cobro del enganche
                        </th>
                        <th scope="col" className={TH}>
                            Estatus
                        </th>
                        <th scope="col" className={TH}>
                            <span className="sr-only">Acción</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortRefunds(refunds).map((refund) => (
                        <tr
                            key={refund.sessionId}
                            className="border-b border-border/60 last:border-b-0"
                        >
                            <td className={`${TD} tabular-nums`}>
                                {formatDateTimeMx(refund.cancelledAt)}
                            </td>
                            <td className={`${TD} font-mono font-medium`}>
                                {refundReference(refund)}
                            </td>
                            <td className={TD}>{refund.sellerName ?? '—'}</td>
                            <td className={`${TD} text-right font-semibold tabular-nums`}>
                                {formatMoneyCents(refund.refundPendingCents)}
                            </td>
                            <td className={TD}>
                                <RefundPayments refund={refund} />
                            </td>
                            <td className={TD}>
                                {refund.status === RefundStatusEnum.PENDING ? (
                                    <StatusPill tone="warning">Pendiente</StatusPill>
                                ) : (
                                    <span className="text-muted-foreground">
                                        {deliveredLabel(refund)}
                                    </span>
                                )}
                            </td>
                            <td className={`${TD} text-right`}>
                                <RefundAction
                                    refund={refund}
                                    canDeliver={canDeliver}
                                    blockedNote={blockedNotes.get(refund.sessionId)}
                                    onDeliver={onDeliver}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function RefundAction({
    refund,
    canDeliver,
    blockedNote,
    onDeliver,
}: {
    refund: SellerPendingRefundItem;
    canDeliver: boolean;
    blockedNote: string | undefined;
    onDeliver: (refund: SellerPendingRefundItem) => void;
}) {
    if (refund.status !== RefundStatusEnum.PENDING) return null;
    if (blockedNote !== undefined) return <Note text={blockedNote} />;
    if (!canDeliver) return null;
    return (
        <Button
            size="sm"
            variant="outline"
            aria-label={`Anotar devolución del reembolso ${refundReference(refund)}`}
            onClick={() => {
                onDeliver(refund);
            }}
        >
            Anotar devolución
        </Button>
    );
}

function RefundPayments({ refund }: { refund: SellerPendingRefundItem }) {
    const { label, amounts } = refundPaymentsLabel(refund);
    if (!amounts) return <>{label}</>;
    return (
        <span className="flex flex-col">
            <span>{label}</span>
            <span className="text-[11px] tabular-nums text-muted-foreground">{amounts}</span>
        </span>
    );
}

function Note({ text }: { text: string }) {
    return <span className="text-[11px] text-muted-foreground">{text}</span>;
}
